import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Clock, Loader2, Phone } from 'lucide-react';
import { BUSINESS, capturePaypalOrder } from '../config';
import { useCart } from '../context/CartContext';

/**
 * Retour de paiement — `/commande/confirmation`.
 *
 * URL de retour à la fois de PayPal (`payment_service.
 * create_reservation_deposit_paypal`) et de Stripe 3DS (`SpecialOrderModal`). Elle
 * n'était déclarée dans aucune route : le client atterrissait sur un en-tête, une
 * zone vide et un pied de page. Ce chemin est celui des redirections PLEINE PAGE —
 * au premier chef la reprise de paiement depuis un lien de relance.
 *
 * La page FINALISE la capture PayPal quand elle le peut. Sur ce chemin, le contexte
 * JavaScript de la boutique a été perdu : `SpecialOrderModal`, qui capture d'habitude
 * dans `onApprove`, n'a jamais tourné. Restait le seul webhook
 * `CHECKOUT.ORDER.APPROVED` — que `paypal_verify_webhook` REFUSE en bloc tant que
 * `PAYPAL_WEBHOOK_ID` n'est pas renseigné. PayPal ajoute `token=<order_id>` à cette
 * URL : on s'en sert pour clore la transaction depuis le navigateur.
 *
 * Trois précautions, toutes trois éprouvées par un test :
 *   - la capture n'est tentée QU'UNE fois (garde `captureEnCours`), sinon un second
 *     rendu rejouerait un appel de paiement ;
 *   - un échec de capture n'annonce JAMAIS un échec de paiement. Le webhook a pu
 *     capturer avant nous, auquel cas PayPal refuse légitimement la seconde capture
 *     alors que le client a bien payé. On dit ce qu'on sait : « pas pu confirmer à
 *     l'instant » ;
 *   - le panier n'est vidé que sur confirmation. Le vider dans le doute effacerait
 *     la commande d'un client qui devrait la reprendre.
 */

const ETAT_EN_COURS = 'en_cours';
const ETAT_CONFIRME = 'confirme';
const ETAT_A_VERIFIER = 'a_verifier';

export default function OrderConfirmation() {
  const [params] = useSearchParams();
  const { clear } = useCart();
  const [etat, setEtat] = useState(ETAT_EN_COURS);
  const [reference, setReference] = useState('');
  // Mémorise la PROMESSE de capture, et non un simple drapeau « déjà tentée ». Voir
  // l'effet ci-dessous : c'est ce qui distingue « ne pas rappeler PayPal » de
  // « ne rien afficher ».
  const captureEnCours = useRef(null);

  // `token` = identifiant d'ordre PayPal ; `redirect_status` = verdict Stripe 3DS.
  const orderId = params.get('token') || '';
  const stripeReussi = params.get('redirect_status') === 'succeeded';

  useEffect(() => {
    document.title = `Confirmation de commande — ${BUSINESS.name}`;
  }, []);

  useEffect(() => {
    // Stripe a déjà tranché côté navigateur : rien à capturer, et surtout pas
    // d'ordre PayPal à réclamer avec un identifiant qui n'en est pas un.
    if (!orderId) {
      if (stripeReussi) {
        setReference(params.get('payment_intent') || '');
        setEtat(ETAT_CONFIRME);
        clear();
      } else {
        setEtat(ETAT_A_VERIFIER);
      }
      return undefined;
    }

    // UN SEUL appel de paiement, même si l'effet est rejoué. En mode strict (React 18
    // en développement, ce que monte `index.js`), le cycle de montage est : effet →
    // nettoyage → effet. Un simple drapeau « déjà tentée » faisait sortir le second
    // passage sans rien faire, alors que le premier venait d'être neutralisé par son
    // propre nettoyage : plus personne n'affichait le verdict et la page restait
    // indéfiniment sur « Confirmation de votre paiement… ».
    //
    // En mémorisant la PROMESSE, le second passage se rebranche sur la capture déjà
    // lancée : PayPal n'est appelé qu'une fois ET le verdict s'affiche. La promesse est
    // indexée sur l'ordre pour ne jamais servir le verdict d'un autre paiement.
    if (captureEnCours.current?.orderId !== orderId) {
      captureEnCours.current = { orderId, promesse: capturePaypalOrder(orderId) };
    }
    const { promesse } = captureEnCours.current;

    let vivant = true;
    (async () => {
      try {
        const reponse = await promesse;
        if (!vivant) return;
        // `parseApiResponse` déballe l'enveloppe `{success, data}` : la réponse vaut
        // directement `{status, order_id}`. Les deux formes sont acceptées, comme
        // dans `ResumeOrder` — cette page ne doit pas retomber en panne silencieuse
        // sur un détail d'enveloppe.
        const statut = reponse?.status || reponse?.data?.status;
        if (statut === 'captured') {
          setReference(reponse?.order_id || reponse?.data?.order_id || orderId);
          setEtat(ETAT_CONFIRME);
          clear();
        } else {
          setReference(orderId);
          setEtat(ETAT_A_VERIFIER);
        }
      } catch {
        if (!vivant) return;
        setReference(orderId);
        setEtat(ETAT_A_VERIFIER);
      }
    })();

    return () => { vivant = false; };
  }, [orderId, stripeReussi, params, clear]);

  return (
    <div
      className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20"
      data-testid="order-confirmation-page"
    >
      <Helmet>
        <title>{`Confirmation de commande — ${BUSINESS.name}`}</title>
        {/* Page de retour propre à une transaction : rien à faire dans un index. */}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {etat === ETAT_EN_COURS && (
        <div className="text-center" data-testid="order-confirmation-encours">
          <Loader2 className="w-8 h-8 text-secondary animate-spin mx-auto mb-4" />
          <p className="text-slate-700">Confirmation de votre paiement…</p>
        </div>
      )}

      {etat === ETAT_CONFIRME && (
        <div className="text-center" data-testid="order-confirmation-confirme">
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="font-heading font-semibold text-2xl md:text-3xl text-primary uppercase tracking-tight mb-3">
            Paiement confirmé
          </h1>
          <p className="text-slate-700 mb-6">
            Merci, votre commande est enregistrée. Vous recevez la confirmation par
            email. Nous vous prévenons dès qu'elle est prête à retirer.
          </p>

          <div className="bg-secondary/5 border border-secondary/15 rounded-sm p-4 mb-8 text-left">
            <p className="flex items-start gap-3 text-sm text-slate-700">
              <Clock className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
              <span>
                Retrait en boutique du lundi au vendredi, {BUSINESS.creneauRetrait} —
                {' '}{BUSINESS.adresse}.
              </span>
            </p>
            {reference && (
              <p className="text-xs text-slate-600 mt-3">Référence : {reference}</p>
            )}
          </div>

          <Sorties />
        </div>
      )}

      {etat === ETAT_A_VERIFIER && (
        <div className="text-center" data-testid="order-confirmation-a-verifier">
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="font-heading font-semibold text-2xl md:text-3xl text-primary uppercase tracking-tight mb-3">
            Paiement en cours de vérification
          </h1>
          {/* Formulation tenue au plus près de ce que l'on sait : on n'a pas pu
              confirmer À L'INSTANT. Le paiement, lui, a très bien pu aboutir. */}
          <p className="text-slate-700 mb-4">
            Nous n'avons pas pu confirmer votre paiement à l'instant. S'il a été
            débité, votre commande est bien enregistrée et vous recevrez la
            confirmation par email — vous n'avez rien à refaire.
          </p>
          <p className="text-slate-700 mb-6">
            Dans le doute, appelez-nous : nous vérifions en direct.
          </p>

          <a
            href={`tel:${BUSINESS.telephoneRaw}`}
            className="inline-flex items-center gap-2 bg-secondary text-black font-bold uppercase tracking-wider rounded-sm px-6 py-3 text-sm mb-8 hover:opacity-90 transition-opacity"
            data-testid="order-confirmation-telephone"
          >
            <Phone className="w-4 h-4" />
            {BUSINESS.telephone}
          </a>

          <Sorties />
        </div>
      )}
    </div>
  );
}

// Aucune des deux issues ne doit être un cul-de-sac : on sort toujours quelque part.
function Sorties() {
  return (
    <div className="flex flex-wrap items-center justify-center gap-4">
      <Link
        to="/catalogue"
        className="text-sm font-semibold text-primary underline underline-offset-4 hover:text-secondary transition-colors"
        data-testid="order-confirmation-catalogue"
      >
        Voir le catalogue
      </Link>
      <Link
        to="/"
        className="text-sm font-semibold text-slate-600 underline underline-offset-4 hover:text-secondary transition-colors"
        data-testid="order-confirmation-accueil"
      >
        Retour à l'accueil
      </Link>
    </div>
  );
}
